/**
 * Componente que decide qué renderizar en `/` según estado de auth.
 *
 * - Mientras carga la sesión (primer render) muestra un spinner mínimo.
 * - Si hay sesión activa:
 *   - Staff (ADR-016, `users.is_staff = TRUE`) → `/admin`. Su rol primario
 *     es operar Kaptar, no operar bajo un municipio.
 *   - Resto → `whenAuthed` (default `/radar`).
 * - Si no hay sesión → renderiza los children (la landing pública).
 *
 * Espera a que la municipality query termine de cargar para no
 * mandar a `/radar` antes de saber si el user es staff y redirigirlo
 * de nuevo (evita un flash de Radar para staff).
 */
import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";

import { useAuth } from "@/lib/auth";
import { useMunicipality } from "@/lib/municipality";

export function AuthedRedirect({
  whenAuthed,
  children,
}: {
  whenAuthed: string;
  children: ReactNode;
}) {
  const { session, isLoading: authLoading } = useAuth();
  const { isStaff, isLoading: muniLoading } = useMunicipality();

  // Si hay sesión, esperamos también la municipality query para saber
  // si es staff antes de decidir destino. Sin esta espera, el staff
  // ve un flash del Radar antes del redirect.
  if (authLoading || (session && muniLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <div className="text-sm text-slate-500">Cargando…</div>
      </div>
    );
  }

  if (session) {
    const target = isStaff ? "/admin" : whenAuthed;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
